import React, { useState } from 'react'
import { Modal, Button, Checkbox, Input } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import client from 'apollo'
import openNotification from 'utils/Notification'
import { DAYS } from 'utils/workingHours'
import { UPDATE_WORKING_HOURS } from '../graphql/Mutation'
import { GET_WORKING_HOURS, GET_WORKING_HOUR_HISTORY } from '../graphql/Query'

const DEFAULT_SLOT = { openTime: '09:00', closeTime: '18:00' }

// Slots are kept as 'HH:mm' strings, the format the native time input and the
// server both use
const toSlotState = (slots) =>
  (slots?.length ? slots : [DEFAULT_SLOT]).map((slot) => ({
    openTime: slot.openTime,
    closeTime: slot.closeTime,
  }))

const dayCircleStyle = (selected) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  border: `1px solid ${selected ? '#1a73e8' : '#d9d9d9'}`,
  background: selected ? '#e8f0fe' : '#fff',
  color: selected ? '#1a73e8' : '#3c4043',
  fontWeight: 500,
  cursor: 'pointer',
})

function EditWorkingHours({ initialDays, workingHours, onClose }) {
  // Pre-fill from the first selected day's current setting
  const source =
    workingHours.find((row) => row.dayOfWeek === initialDays[0]) || {}

  const [selectedDays, setSelectedDays] = useState(initialDays)
  const [isOpen24Hours, setIsOpen24Hours] = useState(!!source.isOpen24Hours)
  const [isClosed, setIsClosed] = useState(!!source.isClosed)
  const [slots, setSlots] = useState(toSlotState(source.slots))
  const [saving, setSaving] = useState(false)
  const timesDisabled = isClosed || isOpen24Hours

  const toggleDay = (day) =>
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    )

  const updateSlot = (index, key, value) =>
    setSlots((prev) =>
      prev.map((slot, i) => (i === index ? { ...slot, [key]: value } : slot))
    )

  const onSave = () => {
    if (!selectedDays.length) {
      openNotification('error', 'Please select at least one day')
      return
    }
    const showTimes = !isClosed && !isOpen24Hours
    if (showTimes && slots.some((s) => !s.openTime || !s.closeTime)) {
      openNotification('error', 'Please set open and close time')
      return
    }

    setSaving(true)
    client
      .mutate({
        mutation: UPDATE_WORKING_HOURS,
        variables: {
          data: {
            daysOfWeek: selectedDays,
            isClosed,
            isOpen24Hours,
            slots: showTimes
              ? slots.map((s) => ({
                  openTime: s.openTime,
                  closeTime: s.closeTime,
                }))
              : [],
          },
        },
        refetchQueries: [
          { query: GET_WORKING_HOURS },
          { query: GET_WORKING_HOUR_HISTORY },
        ],
      })
      .then(() => {
        openNotification('success', 'Working Hours Updated Successfully')
        onClose()
      })
      // Server errors are already shown by the global error link in apollo.js
      .catch((err) => console.log(err))
      .finally(() => setSaving(false))
  }

  return (
    <Modal
      visible
      title='Select days and time'
      onCancel={onClose}
      // The app's global CSS hides .ant-modal-footer, so the buttons live in the body
      footer={null}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 20,
        }}
      >
        {DAYS.map((name, day) => (
          <button
            key={name}
            type='button'
            title={name}
            style={dayCircleStyle(selectedDays.includes(day))}
            onClick={() => toggleDay(day)}
          >
            {name[0]}
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 20 }}>
        <Checkbox
          checked={isOpen24Hours}
          onChange={(e) => {
            setIsOpen24Hours(e.target.checked)
            if (e.target.checked) setIsClosed(false)
          }}
        >
          Open 24 hours
        </Checkbox>
        <Checkbox
          checked={isClosed}
          onChange={(e) => {
            setIsClosed(e.target.checked)
            if (e.target.checked) setIsOpen24Hours(false)
          }}
        >
          Closed
        </Checkbox>
      </div>

      {(isClosed || isOpen24Hours) && (
        <div style={{ color: '#8c8c8c', marginBottom: 12 }}>
          Untick "{isClosed ? 'Closed' : 'Open 24 hours'}" to set open and
          close time.
        </div>
      )}
      <>
          {slots.map((slot, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 12,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: '#5f6368' }}>Open time</div>
                <Input
                  type='time'
                  className='working-hours-openTime'
                  value={slot.openTime || ''}
                  disabled={timesDisabled}
                  onChange={(e) => updateSlot(index, 'openTime', e.target.value)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: '#5f6368' }}>Close time</div>
                <Input
                  type='time'
                  className='working-hours-closeTime'
                  value={slot.closeTime || ''}
                  disabled={timesDisabled}
                  onChange={(e) => updateSlot(index, 'closeTime', e.target.value)}
                />
              </div>
              {slots.length > 1 && !timesDisabled && (
                <CloseOutlined
                  onClick={() =>
                    setSlots((prev) => prev.filter((_, i) => i !== index))
                  }
                />
              )}
            </div>
          ))}
          <Button
            type='link'
            style={{ padding: 0 }}
            disabled={timesDisabled}
            onClick={() =>
              setSlots((prev) => [...prev, { openTime: null, closeTime: null }])
            }
          >
            Add hours
          </Button>
      </>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          marginTop: 24,
        }}
      >
        <Button className='working-hours-cancel' onClick={onClose}>
          Cancel
        </Button>
        <Button
          className='working-hours-save'
          type='primary'
          loading={saving}
          onClick={onSave}
        >
          Save
        </Button>
      </div>
    </Modal>
  )
}

export default EditWorkingHours
